import { useContext, useState } from 'react';
import ModalContext from '../../../../../contexts/ModalContext';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { buildGroupProps } from '../../../../../utils/elementisUtils';
import { createGroup, CreateGroupRequestPayload } from '../../../../../api/my-groups';
import useStudioGroup from '../../../../../hooks/useStudioGroup';
import { SchoolYear } from '../../../hooks/useGroups';
import { useDefaultToast } from '../../../../../hooks/useDefaultToast';
import Alert from '@pelckmans/elementis/components/alert';
import AlertGroup from '@pelckmans/elementis/components/alert-group';
import Button from '@pelckmans/elementis/components/button';
import Dialog from '@pelckmans/elementis/components/dialog';
import Heading from '@pelckmans/elementis/components/heading';
import TextField from '@pelckmans/elementis/components/text-field';
import SchoolSearchSelector from '../../../components/SchoolSearchSelector';
import { UserSchool } from '../../../../../api/schools';

interface Props {
  id: string;
  selectedSchoolYear: SchoolYear;
  onGroupAdded: () => void;
}

interface FormValues {
  courseName: string;
  school: UserSchool | null;
}

export default function AddGroupModal({ id, selectedSchoolYear, onGroupAdded }: Props) {
  const { t } = useTranslation();
  const { closeModal } = useContext(ModalContext);
  const studioGroup = useStudioGroup();
  const [isLoading, setIsLoading] = useState(false);
  const { failedToast, successToast } = useDefaultToast();

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isValid, isSubmitted },
  } = useForm<FormValues>({
    mode: 'all',
  });

  const onSubmit = async (data: FormValues) => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const payload: CreateGroupRequestPayload = {
        name: data.courseName,
        schoolYear: `${selectedSchoolYear.startYear}-${selectedSchoolYear.endYear}`,
        school: {
          id: data.school?.id!,
          isSmartschool: false,
        },
        subjects: [],
      };
      await createGroup(payload, { studioGroup });
      onGroupAdded();
      successToast();
      closeModal(id);
    } catch (err) {
      failedToast();
    }
  };

  return (
    <Dialog id={id} aria-label={t('PAGES.MY_GROUPS.MODALS.ADD_GROUP.A11Y_LABEL')} modal>
      <Dialog.Header
        aria-label={t('PAGES.MY_GROUPS.MODALS.ADD_GROUP.CLOSE.A11Y_LABEL')}
        onClose={() => closeModal(id)}
        title={t('PAGES.MY_GROUPS.MODALS.ADD_GROUP.TITLE', {
          schoolYearStartYear: selectedSchoolYear.startYear,
          schoolYearEndYear: selectedSchoolYear.endYear,
        })}
      />
      <Dialog.Body>
        {isSubmitted && !isValid && (
          <AlertGroup aria-label={undefined} role={undefined}>
            <Alert prominence="danger">{t('ALERTS.INVALID.MESSAGE')}</Alert>
          </AlertGroup>
        )}
        <form id="form-add-group" onSubmit={handleSubmit(onSubmit)}>
          <Heading level={4} removeSpacing="all" className="modal-content-text-title">
            {t('PAGES.MY_GROUPS.MODALS.ADD_GROUP.FORM.TITLE')}
          </Heading>
          <div className="form-fields">
            <TextField
              id="courseName"
              key="courseName"
              labelText={t('PAGES.MY_GROUPS.MODALS.ADD_GROUP.FORM.COURSE_NAME.LABEL')}
              autoComplete="on"
              required
              placeholder={t('PAGES.MY_GROUPS.MODALS.ADD_GROUP.FORM.COURSE_NAME.PLACEHOLDER')}
              error={!!errors.courseName}
              errorText={errors.courseName?.message}
              maxLength={100}
              {...register('courseName', {
                required: t('PAGES.MY_GROUPS.MODALS.ADD_GROUP.FORM.COURSE_NAME.ERROR_REQUIRED'),
              })}
            />

            <Controller
              name="school"
              control={control}
              rules={{ required: t('PAGES.MY_GROUPS.MODALS.ADD_GROUP.FORM.SCHOOL.ERROR_REQUIRED') }}
              render={({ field }) => (
                <SchoolSearchSelector
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  errorText={errors.school?.message}
                />
              )}
            />
          </div>
        </form>
      </Dialog.Body>
      <Dialog.Footer
        buttonGroupPropsRight={buildGroupProps(
          <>
            <Button prominence="neutral" key="cancel" onClick={() => closeModal(id)}>
              {t('ACTIONS.CANCEL')}
            </Button>
            <Button prominence="success" key="create" type="submit" form="form-add-group">
              {t('ACTIONS.CREATE')}
            </Button>
          </>,
        )}
      />
    </Dialog>
  );
}
